/**
 * bills.stalled — tracked bills with no recent floor or committee action.
 * Cuts at min_age_days (default 30) of latest_action_date staleness.
 * Helps the operator notice when bills are dying in committee.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { WidgetProps } from "../registry";
import { Skeleton } from "../../components/ui";

function formatRef(ref: string): string {
  const m = ref.match(/^(\d+)-(hr|s|hres|sres|hjres|sjres|hconres|sconres)-(\d+)$/);
  if (!m) return ref;
  const map: Record<string, string> = {
    hr: "H.R.", s: "S.", hres: "H.Res.", sres: "S.Res.",
    hjres: "H.J.Res.", sjres: "S.J.Res.", hconres: "H.Con.Res.", sconres: "S.Con.Res.",
  };
  return `${map[m[2]] || m[2]} ${m[3]}`;
}

export function BillsStalled({ spec, workspaceId }: WidgetProps) {
  const minAgeDays = (spec.params?.min_age_days as number) ?? 30;
  const limit = (spec.params?.limit as number) ?? 6;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "bills.stalled", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/tracked-bills`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Stalled bills could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" aria-live="polite" className="space-y-2"><span className="sr-only">Loading…</span><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /><Skeleton className="h-3 w-3/5" /></div>;
  const tracked: any[] = data?.tracked_bills ?? [];
  const cutoff = Date.now() - minAgeDays * 86_400_000;
  const stalled = tracked
    .filter((b) => {
      const last = b.bill_meta?.latest_action_date;
      if (!last) return false;
      return new Date(last).getTime() < cutoff;
    })
    .sort((a, b) => new Date(a.bill_meta.latest_action_date).getTime() - new Date(b.bill_meta.latest_action_date).getTime())
    .slice(0, limit);

  if (stalled.length === 0) {
    return <div className="flex items-center gap-1.5 text-xs text-stone-600"><CheckCircle2 size={13} aria-hidden="true" className="text-success" />No stalled bills (no action in {minAgeDays}+ days).</div>;
  }

  return (
    <ul className="space-y-1.5 text-xs">
      <li className="text-stone-500 text-[11px] pb-1 border-b border-stone-100">
        {stalled.length} stalled · no action in {minAgeDays}+ days
      </li>
      {stalled.map((b) => {
        const meta = b.bill_meta || {};
        const days = Math.floor((Date.now() - new Date(meta.latest_action_date).getTime()) / 86_400_000);
        return (
          <li key={b.bill_ref}>
            <Link to={`/bills/${encodeURIComponent(b.bill_ref)}`} className="flex items-center gap-2 hover:bg-stone-50 px-1 py-0.5 rounded -mx-1">
              <span className="font-mono text-[11px] text-stone-500 w-14">{formatRef(b.bill_ref)}</span>
              <span className="flex-1 truncate text-stone-700 text-[12px]">{meta.title || b.bill_ref}</span>
              <span className="font-mono text-amber-700 text-[10px] tabular-nums">{days}d</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
